import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";

let ppEnabled = null;
export const usePayPal = () => {
  const [on, setOn] = useState(ppEnabled);
  useEffect(() => {
    if (ppEnabled === null) api.get("/payments/paypal/config").then((r) => { ppEnabled = r.data.enabled; setOn(ppEnabled); }).catch(() => setOn(false));
  }, []);
  return !!on;
};

export const PayPalButton = ({ onClick, disabled, testId, className = "" }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`inline-flex h-9 items-center justify-center rounded-xl bg-[#FFC439] px-4 text-sm font-bold text-[#003087] transition-[filter] hover:brightness-95 disabled:opacity-50 ${className}`}>
    Pay<span className="text-[#009CDE]">Pal</span>
  </button>
);

export const startCheckout = async (body, provider = "stripe") => {
  const { data } = await api.post(provider === "paypal" ? "/payments/paypal/checkout" : "/payments/checkout", { ...body, origin_url: window.location.origin });
  if (data.checkout_url) window.location.href = data.checkout_url;
  return data;
};

export const PlanCheckoutButton = ({ plan, current, cycle, workspaceId, canManage, hasSub, onChanged }) => {
  const { L } = useApp();
  const [busy, setBusy] = useState(false);
  const paypal = usePayPal();
  const up = plan.price_myr > current.price_myr;
  if (!canManage) return <Button disabled className="mt-6 w-full rounded-xl" data-testid={`upgrade-${plan.key}-btn`}>{L("Hanya pemilik/pentadbir", "Owner/admin only")}</Button>;
  const go = async (provider = "stripe") => {
    setBusy(true);
    try {
      const data = await startCheckout({ workspace_id: workspaceId, kind: "plan", plan: plan.key, cycle }, provider);
      if (data.changed) { toast.success(L(`Pelan ditukar ke ${plan.name}`, `Plan changed to ${plan.name}`)); onChanged(); setBusy(false); }
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  const label = up ? L("Naik taraf", "Upgrade") : L("Tukar pelan", "Change plan");
  const btn = (props) => (
    <Button disabled={busy} variant={up ? "default" : "outline"} className={`mt-6 w-full rounded-xl ${up ? "bg-violet-600 hover:bg-violet-700" : ""}`} data-testid={`upgrade-${plan.key}-btn`} {...props}>
      {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : hasSub ? (up ? <ArrowUp size={15} className="mr-1" /> : <ArrowDown size={15} className="mr-1" />) : <CreditCard size={15} className="mr-1" />} {hasSub ? label : L("Langgan dengan kad", "Subscribe by card")}
    </Button>
  );
  if (!hasSub) return (
    <div>
      {btn({ onClick: () => go("stripe") })}
      {paypal && <PayPalButton onClick={() => go("paypal")} disabled={busy} className="mt-2 h-10 w-full" testId={`paypal-${plan.key}-btn`} />}
    </div>
  );
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{btn({})}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{L(`Tukar ke ${plan.name}?`, `Switch to ${plan.name}?`)}</AlertDialogTitle>
          <AlertDialogDescription>{L("Kad yang disimpan akan dicaj/dikreditkan secara prorata untuk baki tempoh semasa. Kuota baharu berkuat kuasa serta-merta.", "Your saved card will be charged/credited pro-rata for the rest of this period. The new quota applies immediately.")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="plan-switch-cancel-btn">{L("Batal", "Cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => go("stripe")} className="bg-violet-600 hover:bg-violet-700" data-testid="plan-switch-confirm-btn">{L("Sahkan", "Confirm")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
